import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import isLeapYear from 'dayjs/plugin/isLeapYear.js';
import weekday from 'dayjs/plugin/weekday.js';

import { getEasterDate, getHanukkahDate } from './rules';

dayjs.extend(customParseFormat);
dayjs.extend(isLeapYear);
dayjs.extend(weekday);

const dayOfWeekMap = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6
};

function getOccasionDate(occasion, year) {
  switch (occasion?.type_rule?.type?.toLowerCase()) {
    case 'static': {
      const staticDate = dayjs(`${year}-${occasion.month}-${occasion.day}`, 'YYYY-M-D');
      return staticDate.isValid() ? staticDate : null;
    }

    case 'dynamic':
      return getNthWeekdayOfMonth(
        year,
        occasion.month,
        occasion.week,
        occasion.weekday,
        occasion?.type_rule?.rule
      );

    case 'range':
      return getWeekdayAfterDate(year, occasion.month, occasion.after, occasion.weekday);

    case 'floating':
      if (occasion?.type_rule?.rule === 'easter') {
        return getEasterDate(year);
      }
      if (occasion?.type_rule?.rule === 'hanukkah') {
        return getHanukkahDate(year);
      }
      break;

    default:
      return null;
  }
}

function getNthWeekdayOfMonth(year, month, week, weekday, rule) {
  const firstOfMonth = dayjs(`${year}-${month}-01`, 'YYYY-M-D');

  if (rule === 'first_full_week') {
    const firstDayOfWeek = firstOfMonth.day();

    let firstMonday;
    if (firstDayOfWeek !== 1) {
      const daysUntilMonday = (8 - firstDayOfWeek) % 7;
      firstMonday = firstOfMonth.add(daysUntilMonday, 'day');
    } else {
      firstMonday = firstOfMonth;
    }

    return firstMonday;
  }

  if (!weekday) {
    const weekNumber = parseInt(week, 10);
    const startDate = firstOfMonth;

    if (week === 'last') {
      return firstOfMonth.endOf('month').subtract(6, 'day');
    }

    return startDate.add((weekNumber - 1) * 7, 'day');
  }

  const targetWeekday = dayOfWeekMap[weekday.toLowerCase()];
  const firstTargetDay = firstOfMonth.add((7 + targetWeekday - firstOfMonth.day()) % 7, 'day');

  if (week === 'last') {
    const lastDayOfMonth = firstOfMonth.endOf('month');
    return lastDayOfMonth.subtract((7 + lastDayOfMonth.day() - targetWeekday) % 7, 'day');
  }

  return firstTargetDay.add((parseInt(week, 10) - 1) * 7, 'day');
}

function getWeekdayAfterDate(year, month, after, weekday) {
  const afterDate = dayjs(`${year}-${month}-${after.day}`, 'YYYY-M-D');
  const targetWeekday = dayOfWeekMap[weekday?.toLowerCase()];

  if (afterDate.day() === targetWeekday) return afterDate;

  const offset = (7 + targetWeekday - afterDate.day()) % 7;
  return afterDate.add(offset || 7, 'day');
}

function filterDashboardOccasions(occasions) {
  const today = dayjs();
  const year = today.year();

  return occasions?.reduce((acc, occasion) => {
    if (occasion.is_active !== true || !occasion?.collection_id) {
      return acc;
    }
    let startDate;

    try {
      startDate = getOccasionDate(occasion, year);
      if (!startDate) {
        return acc;
      }
    } catch (error) {
      return acc;
    }

    const endDate = startDate.add((occasion.duration || 1) - 1, 'day');

    if (endDate.isBefore(today)) {
      return acc;
    }

    acc.push({
      name: occasion.name,
      date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      emoji: occasion.emoji,
      collection_id: occasion.collection_id
    });

    return acc;
  }, []);
}

const getOrdinalSuffix = day => {
  const number = parseInt(day);
  if (number > 3 && number < 21) return 'th';
  switch (number % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const formatDateWithOrdinal = date => {
  const day = date.format('D');
  const suffix = getOrdinalSuffix(day);
  return `${day}${suffix}`;
};

export { filterDashboardOccasions, formatDateWithOrdinal };
